import React, { useEffect, useState } from "react";
import { AiFillCheckCircle } from "react-icons/ai";
import { useSelector } from "react-redux";
import {
  Col,
  Row,
  UiBox,
  UiCenter,
  UiIcon,
  UiImage,
  UiPrimaryButton,
  UiSkeletonText,
  UiText,
  UiWrap,
  UiWrapItem,
  useBreakpointValue,
} from "~/components/elements";
import { useAppDispatch, useAppSelector, useRequestState } from "~/hooks";
import { fetchSurveys } from "~/redux/dashboards/thunk";
import { RootState } from "~/redux/root-reducer";
import { PageLayout } from "~/components/layouts/PageLayout";
import search from "~/assets/img/v2/surveys/search.png";
import ad from "~/assets/img/v2/surveys/ad.png";
import article from "~/assets/img/v2/surveys/article.png";
import influencer from "~/assets/img/v2/surveys/influencer.png";
import referral from "~/assets/img/v2/surveys/referral.png";
import threedots from "~/assets/img/v2/surveys/threedots.png";
import { useHistory } from "react-router-dom";
import { PAGES } from "~/constants";
import { DashboardMainBox } from "~/components/shared/DashboardMainBox";
import { setConfirmSurvey } from "~/redux/dashboards/slice";

const MAX_VISIBLE_SURVEY_ITEM = 7;
const iconsMap: Record<string, any> = {
  search,
  ad,
  article,
  influencer,
  referral,
  threedots,
};

export function SurveyPage() {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const vrTitle = useBreakpointValue<any>({ base: "headline4", sm: "headline2" });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const data = useSelector((state: RootState) => state.dashboard.surveyData);
  const surveyIds = useSelector((state: RootState) => state.dashboard.confirmSurvey);
  const pendingJobId = useAppSelector(state => state.dashboard.jobId);
  const { loading } = useRequestState(fetchSurveys);

  useEffect(() => {
    setSelectedIds(surveyIds);
  }, [surveyIds]);

  const onClickNext = async () => {
    dispatch(setConfirmSurvey(selectedIds));
    history.push(pendingJobId ? PAGES.SELECT_STYLES : PAGES.SELECT_PACKAGES);
  };

  const onClickBack = async () => {
    history.push(PAGES.DASHBOARD);
  };

  const onClickSurvey = (surveyId: string) => {
    const foundId = selectedIds.find(selectedId => selectedId === surveyId);
    if (foundId) {
      setSelectedIds(selectedIds.filter(selectedId => selectedId !== surveyId));
    } else {
      setSelectedIds([surveyId]);
    }
  };

  return (
    <PageLayout
      metaTagsConfig={{
        title: `Bettershot - Surveys`,
        description: `Get 120 professional photoshoots in 3 hours using AI. Elevate your personal brand with corporate photoshoots.`,
        charset: "utf8",
        lang: "en",
        metas: [
          {
            name: "keywords",
            content: "corporate photoshoot,professional photoshoot,bettershot ai,linkedin profile photo,ai photoshoot",
          },
          { name: "robots", content: "noindex,nofollow" },
        ],
        links: [{ rel: "canonical", href: window.location.href }],
      }}
      as={Col}
      flex={1}
      isInGeneratingStep
    >
      <Col flex={1} justify="center">
        <UiCenter p={4}>
          <DashboardMainBox>
            <UiText vr={vrTitle} textAlign="center">
              Where did you hear about us?
            </UiText>
            <UiText vr="body1" mt={{ base: 2, sm: 3 }} textAlign="center">
              Before we get started, can you tell us how you heard about Bettershot?
            </UiText>
            <UiBox mt={{ base: 4, sm: 8 }}>
              <UiSkeletonText noOfLines={{ base: 9, md: 5 }} skeletonHeight={10} isLoaded={!loading}>
                <UiWrap spacing={{ base: 2, sm: 5 }} justify="center">
                  {data.map(survey => {
                    const isSelected = selectedIds.includes(survey.id);
                    return (
                      <UiWrapItem key={survey.id} w={{ base: "full", sm: "auto" }}>
                        <Col
                          flexDirection={{ base: "row", sm: "column" }}
                          align="center"
                          w={{ base: "full", sm: "14.68rem" }}
                          h={{ base: "3.5rem", sm: "100%" }}
                          bg={isSelected ? "lightblue" : "neutral.gray00"}
                          borderColor={isSelected ? "blue" : "transparent"}
                          borderWidth="1px"
                          rounded="2xl"
                          p={{ base: 4, sm: 5 }}
                          cursor="pointer"
                          onClick={() => onClickSurvey(survey.id)}
                          position="relative"
                        >
                          <UiImage
                            src={iconsMap[survey.icon_name] ?? threedots}
                            width={{ base: "1.5rem", sm: "2.25rem" }}
                          />
                          <UiText
                            vr="title1"
                            textAlign="center"
                            px={{ base: 0, sm: 5 }}
                            mt={{ base: 0, sm: 4 }}
                            ml={{ base: 3, sm: 0 }}
                          >
                            {survey.value}
                          </UiText>
                          {selectedIds.includes(survey.id) && (
                            <UiIcon
                              color="blue"
                              as={AiFillCheckCircle}
                              boxSize={6}
                              mx={0}
                              pos="absolute"
                              right={3}
                              top={{ base: undefined, sm: 3 }}
                            />
                          )}
                        </Col>
                      </UiWrapItem>
                    );
                  })}
                </UiWrap>
              </UiSkeletonText>
            </UiBox>

            <Row
              gap={{ base: 2, sm: 5 }}
              flexDirection={{ base: "column", sm: "row-reverse" }}
              align="center"
              justify="center"
              mt={{ base: 4, md: 8 }}
              w="full"
            >
              <UiPrimaryButton
                w="full"
                maxW={{ base: undefined, sm: "16.25rem" }}
                isDisabled={!selectedIds.length}
                onClick={onClickNext}
              >
                Submit
              </UiPrimaryButton>
              <UiPrimaryButton
                w="full"
                maxW={{ base: undefined, sm: "16.25rem" }}
                variant="outline"
                borderColor="darkblue"
                bg="transparent"
                color="darkblue"
                _hover={{
                  bg: "transparent",
                  color: "darkblue",
                }}
                onClick={onClickBack}
              >
                <UiText vr="title1" color="darkblue">
                  Back
                </UiText>
              </UiPrimaryButton>
            </Row>
          </DashboardMainBox>
        </UiCenter>
      </Col>
    </PageLayout>
  );
}
